import { LDContext, LDContextCommon, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';

type MultiContext = { kind: 'multi'; [segment: string]: LDContextCommon | 'multi' };

const normalize = (ctx?: LDContext): MultiContext => {
  if (!ctx) return { kind: 'multi' };
  if ('kind' in ctx && ctx.kind === 'multi') {
    return ctx as MultiContext;
  }

  const kind = 'kind' in ctx && typeof ctx.kind === 'string' ? ctx.kind : 'user';
  return { kind: 'multi', [kind]: ctx };
};

/**
 * useUpdateLDContext is a custom hook that will add to the multi context for LaunchDarkly by taking in a contextName and a key value for it and adding it to the original context
 * @param {string} contextName the name of the context you want to add
 * @param {string | null | undefined} key the value for the context key
 *
 * Learn more about multi context  {@link https://docs.launchdarkly.com/home/observability/multi-contexts#multi-contexts}
 */
export function useUpdateLDContext(contextName: string, key?: string | null) {
  const ldClient = useLDClient();

  useEffect(() => {
    const updateContext = async (contextName: string, key?: string) => {
      if (ldClient) {
        await ldClient.waitUntilReady();
        const normalized = normalize(ldClient.getContext());
        const existing = normalized[contextName] as LDContextCommon | undefined;
        if (existing?.key === key) return;

        const next = {
          ...normalized,
          [contextName]: { ...(existing ?? {}), key },
        } as MultiContext;

        await ldClient.identify(next as LDContext);
      }
    };

    if (key) {
      updateContext(contextName, key);
    }
  }, [ldClient, contextName, key]);
}
